'use client';
import React, { useEffect, useState } from 'react';
import Icon from '@/components/ui/AppIcon';
import { createClient } from '@/lib/supabase/client';

interface Category {
  name: string;
  icon: string;
  color: string;
  campaigns: number;
}

const iconMap: Record<string, string> = {
  Electronics: 'DevicePhoneMobileIcon',
  Beauty: 'SparklesIcon',
  'Beauty & Skincare': 'SparklesIcon',
  'Hair Care': 'StarIcon',
  'Food & Beverages': 'ShoppingBagIcon',
  'Home & Kitchen': 'HomeIcon',
  'Fitness & Sports': 'TrophyIcon',
  Fitness: 'TrophyIcon',
  Wearables: 'WatchIcon',
  'Books & Stationery': 'BookOpenIcon',
  Books: 'BookOpenIcon',
  'Baby & Kids': 'HeartIcon',
  'Pet Supplies': 'HeartIcon',
  'Apparel & Fashion': 'TagIcon',
  'Health & Wellness': 'ShieldCheckIcon',
};

const colorMap: Record<string, string> = {
  Electronics: '#1565C0',
  Beauty: '#AD1457',
  'Beauty & Skincare': '#AD1457',
  'Hair Care': '#7B1FA2',
  'Food & Beverages': '#E65100',
  'Home & Kitchen': '#2E7D32',
  'Fitness & Sports': '#1565C0',
  Fitness: '#1565C0',
  Wearables: '#FF6B35',
  'Books & Stationery': '#4527A0',
  Books: '#4527A0',
  'Baby & Kids': '#C62828',
  'Pet Supplies': '#00695C',
  'Apparel & Fashion': '#F57C00',
  'Health & Wellness': '#00C853',
};

const fallbackCategories: Category[] = [
  { name: 'Electronics', icon: 'DevicePhoneMobileIcon', color: '#1565C0', campaigns: 0 },
  { name: 'Beauty & Skincare', icon: 'SparklesIcon', color: '#AD1457', campaigns: 0 },
  { name: 'Hair Care', icon: 'StarIcon', color: '#7B1FA2', campaigns: 0 },
  { name: 'Food & Beverages', icon: 'ShoppingBagIcon', color: '#E65100', campaigns: 0 },
  { name: 'Home & Kitchen', icon: 'HomeIcon', color: '#2E7D32', campaigns: 0 },
  { name: 'Fitness & Sports', icon: 'TrophyIcon', color: '#1565C0', campaigns: 0 },
  { name: 'Wearables', icon: 'WatchIcon', color: '#FF6B35', campaigns: 0 },
  { name: 'Books & Stationery', icon: 'BookOpenIcon', color: '#4527A0', campaigns: 0 },
  { name: 'Baby & Kids', icon: 'HeartIcon', color: '#C62828', campaigns: 0 },
  { name: 'Pet Supplies', icon: 'HeartIcon', color: '#00695C', campaigns: 0 },
  { name: 'Apparel & Fashion', icon: 'TagIcon', color: '#F57C00', campaigns: 0 },
  { name: 'Health & Wellness', icon: 'ShieldCheckIcon', color: '#00C853', campaigns: 0 },
];

export default function CategoryInterests() {
  const [categories, setCategories] = useState<Category[]>(fallbackCategories);

  useEffect(() => {
    async function fetchCategories() {
      const supabase = createClient();
      try {
        const [catRes, campaignRes] = await Promise.all([
          supabase
            .from('product_categories')
            .select('name, icon')
            .eq('is_active', true)
            .order('sort_order', { ascending: true }),
          supabase
            .from('campaigns')
            .select('category')
            .eq('campaign_status', 'active'),
        ]);

        if (catRes.error || !catRes.data || catRes.data.length === 0) return;

        const countMap: Record<string, number> = {};
        campaignRes.data?.forEach((c) => {
          countMap[c.category] = (countMap[c.category] || 0) + 1;
        });

        setCategories(
          catRes.data.map((cat) => ({
            name: cat.name,
            icon: iconMap[cat.name] || cat.icon || 'TagIcon',
            color: colorMap[cat.name] || '#FF6B35',
            campaigns: countMap[cat.name] || 0,
          }))
        );
      } catch (err) {
        console.log('Categories fetch error:', err);
      }
    }
    fetchCategories();
  }, []);

  return (
    <section className="section-py bg-background">
      <div className="max-w-7xl mx-auto px-4 sm:px-6">
        <div className="text-center mb-10">
          <span className="inline-block text-xs font-semibold uppercase tracking-widest text-primary bg-primary-container px-3 py-1 rounded-full mb-3">
            Categories
          </span>
          <h2 className="font-heading font-bold text-foreground" style={{ fontSize: 'clamp(1.5rem, 3vw, 2rem)' }}>
            Review What You Love
          </h2>
          <p className="text-muted-foreground mt-2 text-sm">
            {categories.length} product categories across India's top marketplaces
          </p>
        </div>

        <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-4 xl:grid-cols-6 gap-4">
          {categories.map((cat) => (
            <div
              key={cat.name}
              className="bg-card rounded-2xl border border-border p-4 text-center card-hover cursor-pointer group"
            >
              <div
                className="w-12 h-12 rounded-2xl flex items-center justify-center mx-auto mb-3 transition-transform group-hover:scale-110"
                style={{ backgroundColor: `${cat.color}15` }}
              >
                <Icon name={cat.icon as 'DevicePhoneMobileIcon'} size={22} style={{ color: cat.color }} />
              </div>
              <div className="font-heading font-semibold text-foreground text-xs leading-tight">{cat.name}</div>
              <div className="text-[10px] text-muted-foreground mt-1">{cat.campaigns} campaigns</div>
            </div>
          ))}
        </div>
      </div>
    </section>
  );
}